// src/components/ImageUploader.js
import { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { uploadMultipleImages } from "../utils/api";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";

export default function ImageUploader({ images, setImages, maxImages = 6 }) {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ index: 0, total: 0, pct: 0 });

  const onDrop = useCallback(async (acceptedFiles) => {
    const remaining = maxImages - images.length;
    if (remaining <= 0) {
      toast.error(`Maximum ${maxImages} images allowed`);
      return;
    }
    const files = acceptedFiles.slice(0, remaining);
    setUploading(true);
    try {
      const urls = await uploadMultipleImages(files, user.uid, setProgress);
      setImages(prev => [...prev, ...urls]);
      toast.success(`${urls.length} image(s) uploaded!`);
    } catch {
      toast.error("Image upload failed. Please try again.");
    } finally {
      setUploading(false);
      setProgress({ index: 0, total: 0, pct: 0 });
    }
  }, [images, user, maxImages, setImages]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [".jpg", ".jpeg", ".png", ".webp"] },
    disabled: uploading || images.length >= maxImages,
    maxSize: 5 * 1024 * 1024, // 5MB
  });

  const removeImage = (idx) => {
    setImages(prev => prev.filter((_, i) => i !== idx));
  };

  return (
    <div>
      {/* Dropzone */}
      <div {...getRootProps()} style={{
        border: `2px dashed ${isDragActive ? "#1a6b3c" : "#e2ece2"}`,
        borderRadius: 12, padding: 24, textAlign: "center", cursor: "pointer",
        background: isDragActive ? "#f0fff4" : "#fafafa",
        transition: "all 0.2s",
        opacity: images.length >= maxImages ? 0.5 : 1,
      }}>
        <input {...getInputProps()} />
        <div style={{ fontSize: 32, marginBottom: 8 }}>📷</div>
        {uploading ? (
          <>
            <p style={{ color: "#1a6b3c", fontWeight: 600, fontSize: 14 }}>
              Uploading image {progress.index + 1} of {progress.total}...
            </p>
            <div style={{ background: "#e2ece2", borderRadius: 10, height: 8, margin: "10px auto", maxWidth: 220 }}>
              <div style={{ background: "#1a6b3c", borderRadius: 10, height: 8, width: `${progress.pct}%`, transition: "width 0.2s" }} />
            </div>
            <p style={{ fontSize: 12, color: "#6b7c6b" }}>{progress.pct}%</p>
          </>
        ) : (
          <>
            <p style={{ fontWeight: 600, fontSize: 14, color: "#1a2e1a" }}>
              {isDragActive ? "Drop images here" : "Click or drag images here"}
            </p>
            <p style={{ fontSize: 12, color: "#6b7c6b", marginTop: 4 }}>
              JPG, PNG, WebP · Max 5MB each · Up to {maxImages} images
            </p>
            <p style={{ fontSize: 12, color: "#1a6b3c", marginTop: 4, fontWeight: 600 }}>
              {images.length}/{maxImages} uploaded
            </p>
          </>
        )}
      </div>

      {/* Preview grid */}
      {images.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(90px, 1fr))", gap: 8, marginTop: 12 }}>
          {images.map((url, i) => (
            <div key={i} style={{ position: "relative", borderRadius: 8, overflow: "hidden", aspectRatio: "1" }}>
              <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              {i === 0 && (
                <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "rgba(26,107,60,0.85)", color: "white", fontSize: 10, fontWeight: 700, textAlign: "center", padding: "2px 0" }}>Cover</div>
              )}
              <button onClick={() => removeImage(i)} style={{
                position: "absolute", top: 4, right: 4, width: 22, height: 22,
                background: "#c0392b", border: "none", borderRadius: 11, color: "white",
                fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center"
              }}>×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
